import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NotistackServices, PublicTeamDTO, PublicWorkspaceDTO, WorkspaceServices, WSServices } from "../services";
import { HandleError } from "shared/utils";

interface UseWorkspaceDataResult {
	workspace: PublicWorkspaceDTO | null;
	loadingWorkspace: boolean;
	teams: PublicTeamDTO[];
	loadingTeams: boolean;
}

export const useWorkspaceData = (): UseWorkspaceDataResult => {
	const [workspace, setWorkspace] = useState<PublicWorkspaceDTO | null>(null);
	const [loadingWorkspace, setLoadingWorkspace] = useState(true);
	const [teams, setTeams] = useState<PublicTeamDTO[]>([]);
	const [loadingTeams, setLoadingTeams] = useState(false);
	const teamsRefreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const loadTeamsByWorkspaceId = useCallback(async (workspaceId: string, options?: { withLoading?: boolean; silent?: boolean }) => {
		const withLoading = options?.withLoading === true;
		const silent = options?.silent === true;

		if (withLoading) {
			setLoadingTeams(true);
		}

		try {
			const teamsData = await WorkspaceServices.listTeams(workspaceId);
			setTeams(teamsData);
		} catch (error) {
			if (!silent) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar equipes");
			}
		} finally {
			if (withLoading) {
				setLoadingTeams(false);
			}
		}
	}, []);

	const scheduleTeamsRefresh = useCallback(
		(workspaceId: string) => {
			if (teamsRefreshDebounceRef.current) {
				clearTimeout(teamsRefreshDebounceRef.current);
			}

			teamsRefreshDebounceRef.current = setTimeout(() => {
				void loadTeamsByWorkspaceId(workspaceId, { silent: true });
			}, 350);
		},
		[loadTeamsByWorkspaceId],
	);

	useLayoutEffect(() => {
		let cancelled = false;
		setLoadingWorkspace(true);

		const timer = setTimeout(async () => {
			try {
				const data = await WorkspaceServices.listWorkspaces();
				if (cancelled) return;

				if (data.length === 0) {
					setWorkspace(null);
					return;
				}

				const sorted = [...data].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
				setWorkspace(sorted[0]);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar workspace");
			} finally {
				if (!cancelled) setLoadingWorkspace(false);
			}
		}, 200);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, []);

	useEffect(() => {
		if (!workspace) {
			setTeams([]);
			setLoadingTeams(false);
			if (teamsRefreshDebounceRef.current) {
				clearTimeout(teamsRefreshDebounceRef.current);
				teamsRefreshDebounceRef.current = null;
			}
			return;
		}

		void loadTeamsByWorkspaceId(String(workspace.id), { withLoading: true });
	}, [workspace, loadTeamsByWorkspaceId]);

	useEffect(() => {
		if (!workspace) return;

		const workspaceId = String(workspace.id);

		const workspaceTeamsSubscription = WSServices.onReceive<{ type?: string }>(`workspace:${workspaceId}:teams`, (data) => {
			if (data?.type === "team_created" || data?.type === "team_deleted") {
				scheduleTeamsRefresh(workspaceId);
			}
		});

		const teamMembersSubscriptions = teams.map((team) => {
			return WSServices.onReceive<{ type?: string }>(`team:${team.id}:members`, (data) => {
				if (data?.type === "member_hired" || data?.type === "member_removed") {
					scheduleTeamsRefresh(workspaceId);
				}
			});
		});

		return () => {
			workspaceTeamsSubscription.stop();
			teamMembersSubscriptions.forEach((subscription) => subscription.stop());

			if (teamsRefreshDebounceRef.current) {
				clearTimeout(teamsRefreshDebounceRef.current);
				teamsRefreshDebounceRef.current = null;
			}
		};
	}, [workspace, teams, scheduleTeamsRefresh]);

	useEffect(() => {
		return () => {
			if (teamsRefreshDebounceRef.current) {
				clearTimeout(teamsRefreshDebounceRef.current);
				teamsRefreshDebounceRef.current = null;
			}
		};
	}, []);

	return {
		workspace,
		loadingWorkspace,
		teams,
		loadingTeams,
	};
};
